import React from "react";

import PictureTags from "./PictureTags";
import UploadZone, { UPLOAD_ZONE_IMAGE_HEIGHT } from "./UploadZone";

import type { JSX } from "react";

import type {
  ImageRatio,
  Translation,
} from "./ModuleImageUploadWithTranslation";
import type { PictureKeys } from "../../constants/ratio";
import type {
  TagDeleteFunction,
  TagState,
  TagUpdateFunction,
} from "../../hooks/usePictureTags";

interface PictureRatioProps<T extends Translation> {
  translation: T;
  imageRatio: ImageRatio;
  isDisabled?: boolean;
  isLoading?: boolean;
  handleDrop: (params: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }) => void;
  onTagDelete: TagDeleteFunction;
  onTagUpdate: TagUpdateFunction;
  tagState: TagState;
}

export default function PictureRatio<T extends Translation>({
  translation,
  imageRatio,
  isDisabled,
  isLoading,
  handleDrop,
  onTagDelete,
  onTagUpdate,
  tagState,
}: PictureRatioProps<T>): JSX.Element {
  const imagePreview = translation.picture
    ? translation.picture[imageRatio.id].url
    : null;

  const imageTags = translation.picture
    ? translation.picture[imageRatio.id].tags
    : null;

  const convertRatioToNumber = (ratio: string): number => {
    const [width, height] = ratio.split(" / ").map(Number);
    return width && height ? width / height : 1;
  };

  const imageWidth =
    UPLOAD_ZONE_IMAGE_HEIGHT * convertRatioToNumber(imageRatio.ratio);

  return (
    <div className="relative flex flex-col gap-[0.15625rem]">
      <UploadZone
        imagePreview={imagePreview}
        imageRatio={imageRatio}
        isDisabled={isDisabled}
        isLoading={isLoading}
        onDrop={handleDrop}
        translationId={translation.uuid}
      />
      <div className="text-foreground text-center text-[0.78125rem] font-medium">
        {imageRatio.label}
      </div>
      <PictureTags
        onTagDelete={onTagDelete}
        onTagUpdate={onTagUpdate}
        ratioId={imageRatio.id}
        tagList={imageTags}
        tagState={tagState}
        translationId={translation.uuid}
      />
      {imageRatio.subLabel ? (
        <div
          className="text-muted-foreground text-center text-[0.78125rem]"
          style={{
            width: `${imageWidth}px`,
          }}
        >
          {imageRatio.subLabel}
        </div>
      ) : null}
    </div>
  );
}
